// src/state/modules/auth.js
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { refreshSession } from "@/services/authSession";

const configuredApiBaseUrl = (process.env.VUE_APP_API_BASE_URL || "")
  .trim()
  .replace(/\/+$/, "");

const API_BASE_URL = configuredApiBaseUrl || (
  process.env.NODE_ENV === "development"
    ? "https://localhost:7204"
    : ""
);

function isTokenExpired(token) {
  try {
    const decoded = jwtDecode(token);
    const exp = decoded?.exp; // seconds
    if (!exp) return false;
    return exp <= Math.floor(Date.now() / 1000);
  } catch (e) {
    return true;
  }
}

function extractRolesFromPayload(payload) {
  const roleKeys = Object.keys(payload).filter((k) =>
    k.toLowerCase().includes("role")
  );

  const roles = [];
  for (const key of roleKeys) {
    const val = payload[key];
    if (Array.isArray(val)) roles.push(...val);
    else if (typeof val === "string") roles.push(val);
  }
  return [...new Set(roles)];
}

function extractRolesFromToken(token) {
  try {
    const decoded = jwtDecode(token);
    return extractRolesFromPayload(decoded);
  } catch (error) {
    return [];
  }
}
function extractUserFromToken(token) {
  try {
    const decoded = jwtDecode(token);

    // sık görülen claim isimleri
    const name =
      decoded.fullname ||
      decoded.fullName ||
      decoded.name ||
      decoded.unique_name ||
      decoded.given_name ||
      decoded.family_name ||
      decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];

    const email =
      decoded.email ||
      decoded.upn ||
      decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"];

    return {
      name: name || null,
      email: email || null,
    };
  } catch {
    return null;
  }
}

const legacyAuthStorageKeys = [
  "access_token",
  "refresh_token",
  "roles",
  "auth_user",
];

function clearLegacyAuthStorage() {
  legacyAuthStorageKeys.forEach((key) => localStorage.removeItem(key));
}

clearLegacyAuthStorage();


// --- STATE ---
export const state = () => {
  return {
    // Access token yalnızca uygulama belleğinde tutulur. Refresh token ise
    // JavaScript'in erişemediği HttpOnly cookie içindedir.
    accessToken: "",
    roles: [],
    user: null,
    // null: henüz kontrol edilmedi, false: abonelik kilitli, true: erişim açık
    subscriptionCanUseSystem: null,
    subscriptionAccessCheckedAt: 0,
  };
};

// --- GETTERS ---
export const getters = {
  // ✅ “login olmuş mu” = access token var mı (expired olabilir)
  hasAccessToken: (state) => !!state.accessToken,

  // ✅ token geçerli mi
  isAccessTokenValid: (state) =>
    !!state.accessToken && !isTokenExpired(state.accessToken),

  // (eski isimle uyum için)
  isAuthenticated: (state) =>
    !!state.accessToken && !isTokenExpired(state.accessToken),

  roles: (state) => state.roles,
  hasRole: (state) => (role) => state.roles.includes(role),
  hasAnyRole: (state) => (required) => required.some((r) => state.roles.includes(r)),

  user: (state) => state.user,
  userName: (state) =>
    state.user?.fullName ||
    state.user?.name ||
    state.user?.username ||
    state.user?.email ||
    "_",
  userTitle: (state) =>
    state.user?.title || state.user?.roleName || state.roles?.[0] || "-",
  subscriptionCanUseSystem: (state) => state.subscriptionCanUseSystem,
  subscriptionAccessCheckedAt: (state) => state.subscriptionAccessCheckedAt,
};

// --- MUTATIONS ---
export const mutations = {
  SET_USER(state, user) {
    state.user = user ?? null;
  },

  SET_SUBSCRIPTION_ACCESS(state, canUseSystem) {
    state.subscriptionCanUseSystem = Boolean(canUseSystem);
    state.subscriptionAccessCheckedAt = Date.now();
  },

  SET_TOKENS(state, { accessToken, roles, user }) {
    state.accessToken = accessToken || "";
    state.roles = roles || [];
    state.user = user ?? null;
  },

  LOGOUT(state) {
    state.accessToken = "";
    state.roles = [];
    state.user = null;
    state.subscriptionCanUseSystem = null;
    state.subscriptionAccessCheckedAt = 0;
    clearLegacyAuthStorage();
  },
};

// --- ACTIONS ---
export const actions = {
  updateCurrentUser({ commit }, user) {
    commit("SET_USER", user);
  },

  updateSubscriptionAccess({ commit }, canUseSystem) {
    commit("SET_SUBSCRIPTION_ACCESS", canUseSystem);
  },

  loginSuccess({ commit }, tokenResponse) {
    const accessToken = tokenResponse.accessToken || tokenResponse.AccessToken;

    const roles = accessToken
      ? extractRolesFromToken(accessToken).map((r) => String(r))
      : [];
    const userFromApi = tokenResponse.user || tokenResponse.User || null;
    const userFromToken = accessToken ? extractUserFromToken(accessToken) : null;
    commit("SET_TOKENS", {
      accessToken,
      roles,
      user: userFromApi || userFromToken || null,
    });
  },

  // HttpOnly cookie tarayıcı tarafından otomatik gönderilir.
  async refresh({ dispatch }) {
    const resp = await refreshSession();

    await dispatch("loginSuccess", resp.data);
    return resp.data;
  },

  // ✅ BURASI: API'ye logout isteği
  async logout({ commit }) {
    try {
      await axios.post(
        `${API_BASE_URL}/api/auths/logout`,
        null,
        { withCredentials: true }
      );
    } catch (e) {
      // Sunucuya ulaşılamasa da bellekteki access token temizlenir.
    } finally {
      commit("LOGOUT");
    }
  },
};
